import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Navigation, ArrowRight, CheckCircle, CarFront, Navigation2, Clock, MapPinned, Phone, RefreshCw } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet icon issue
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow
});
L.Marker.prototype.options.icon = DefaultIcon;

// Custom Icons for Map (Green for Depa, Red for Destinasyon, Car for Chofè)
const greenMarkerIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const redMarkerIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const carMarkerIcon = L.icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/3721/3721619.png',
    iconSize: [35, 35],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17]
});

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Haversine formula
const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371; 
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c;
};

// Map Click Handler Component
const LocationMarker = ({ position, setPosition, icon, label }) => {
    const map = useMapEvents({
        click(e) {
            setPosition(e.latlng);
            map.flyTo(e.latlng, map.getZoom());
        },
    });

    useEffect(() => {
        if (position) {
             map.flyTo(position, 15);
        }
    }, [position, map]);

    return position === null ? null : (
        <Marker position={position} icon={icon || greenMarkerIcon}>
            {label && <Popup>{label}</Popup>}
        </Marker>
    );
};

// Map Bounds Manager
const MapBoundsManager = ({ pickup, dropoff, driver }) => {
    const map = useMap();
    useEffect(() => {
        const points = [];
        if (pickup && pickup.lat) points.push([pickup.lat, pickup.lng]);
        if (dropoff && dropoff.lat) points.push([dropoff.lat, dropoff.lng]);
        if (driver && driver.lat) points.push([driver.lat, driver.lng]);
        
        if (points.length > 1) {
            map.fitBounds(points, { padding: [50, 50] });
        } else if (points.length === 1) {
            map.setView(points[0], 15);
        }
    }, [pickup, dropoff, driver, map]);
    return null;
};

export const ClientTransport = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('booking'); // 'booking' | 'history'
    const [step, setStep] = useState(1);
    
    // Core Data
    const [originCoords, setOriginCoords] = useState({ lat: 18.5524, lng: -72.2891 }); // PAP
    const [originSelected, setOriginSelected] = useState(false);
    const [originAddress, setOriginAddress] = useState("");
    
    const [destCoords, setDestCoords] = useState({ lat: 18.5524, lng: -72.2891 });
    const [destSelected, setDestSelected] = useState(false);
    const [destAddress, setDestAddress] = useState("");
    
    const [transportType, setTransportType] = useState('moto'); // 'moto' or 'oto'
    
    // User details
    const cachedUser = JSON.parse(localStorage.getItem("buyer_user") || "null");
    const [clientName, setClientName] = useState(cachedUser?.name || cachedUser?.email || "");
    const [clientPhone, setClientPhone] = useState(localStorage.getItem("buyer_phone") || cachedUser?.phone || "");
    
    // Tracking & History
    const [rideId, setRideId] = useState(new URLSearchParams(window.location.search).get('rideId') || null);
    const [trackingRide, setTrackingRide] = useState(null);
    const [rideHistory, setRideHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [phoneInput, setPhoneInput] = useState(clientPhone || "");
    
    // Option 5: Booking for someone else
    const [isForSomeoneElse, setIsForSomeoneElse] = useState(false);
    const [passengerName, setPassengerName] = useState("");
    const [passengerPhone, setPassengerPhone] = useState("");

    // Option 6: Scheduled ride
    const [scheduleType, setScheduleType] = useState("now"); // "now" or "later"
    const [scheduledDate, setScheduledDate] = useState(() => {
        const d = new Date();
        return d.toISOString().split('T')[0];
    });
    const [scheduledTime, setScheduledTime] = useState("08:00");
    
    const [processing, setProcessing] = useState(false);
    const [onlineDrivers, setOnlineDrivers] = useState([]);
    const [showDriversList, setShowDriversList] = useState(false);

    const fetchOnlineDrivers = async () => {
        try {
            const res = await axios.get(`${API}/drivers/list-active?_t=${Date.now()}`);
            if (res.data?.success) {
                setOnlineDrivers(res.data.drivers || []);
            }
        } catch (e) {}
    };

    useEffect(() => {
        fetchOnlineDrivers();
        const interval = setInterval(fetchOnlineDrivers, 10000);
        return () => clearInterval(interval);
    }, []);

    // Live Ride Tracking polling
    useEffect(() => {
        if (!rideId) return;
        
        const fetchStatus = async () => {
            try {
                const res = await axios.get(`${API}/order-status/${rideId}`);
                setTrackingRide(res.data);
            } catch (err) {
                console.error("Erè nan swivi kous:", err);
            }
        };

        fetchStatus();
        const interval = setInterval(fetchStatus, 5000);
        return () => clearInterval(interval);
    }, [rideId]);

    // Calculations
    const distance = originSelected && destSelected ? haversineKm(originCoords.lat, originCoords.lng, destCoords.lat, destCoords.lng) : 0;
    
    const calculatePrices = () => {
        if (!distance) return { base: 0, fee: 0, total: 0 };
        let baseRate = transportType === 'oto' ? 45 : 25;
        let minPrice = transportType === 'oto' ? 300 : 100;
        let base = Math.ceil(distance * baseRate);
        if (base < minPrice) base = minPrice;
        
        let fee = Math.ceil(base * 0.05); // 5% System fee
        return { base, fee, total: base + fee };
    };
    
    const { base, fee, total: price } = calculatePrices();
    const formatPrice = (p) => new Intl.NumberFormat('fr-HT', { style: 'decimal', minimumFractionDigits: 0 }).format(p);

    const handleCheckout = async (e) => {
        e.preventDefault();
        if (!clientName || !clientPhone) return toast.error("Tanpri mete non ak telefòn ou.");
        
        setProcessing(true);
        try {
            if (isForSomeoneElse && (!passengerName || !passengerPhone)) {
                toast.error("Tanpri mete non ak nimewo telefòn moun k ap vwayaje a!");
                setProcessing(false);
                return;
            }

            const res = await axios.post(`${API}/client/transport/request`, {
                client_name: clientName,
                client_phone: clientPhone,
                pickup_address: originAddress || "Sou Map La",
                pickup_lat: originCoords.lat,
                pickup_lng: originCoords.lng,
                dropoff_address: destAddress || "Sou Map La",
                dropoff_lat: destCoords.lat,
                dropoff_lng: destCoords.lng,
                distance_km: distance,
                price_htg: price,
                is_for_someone_else: isForSomeoneElse,
                passenger_name: isForSomeoneElse ? passengerName : clientName,
                passenger_phone: isForSomeoneElse ? passengerPhone : clientPhone,
                is_scheduled: scheduleType === "later",
                scheduled_date: scheduleType === "later" ? scheduledDate : null,
                scheduled_time: scheduleType === "later" ? scheduledTime : null
            });
            
            if (res.data.redirect_url) {
                toast.success("Ap redirije w sou Moncash...");
                window.location.href = res.data.redirect_url;
            } else if (res.data.success) {
                toast.success("Kous la voye! Yon chofè ap jwenn li kounye a.");
                setRideId(res.data.order_id);
                // Update URL to include rideId
                const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + `?rideId=${res.data.order_id}`;
                window.history.pushState({ path: newUrl }, '', newUrl);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erè pandan demann nan.");
        } finally {
            setProcessing(false);
        }
    };
    
    const getMyGPS = (isOrigin = true) => {
        if (!navigator.geolocation) return toast.error("Aparèy ou pa sipòte GPS");
        toast.info("Aplikasyon an ap pran GPS ou...");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                if (isOrigin) { setOriginCoords(c); setOriginSelected(true); }
                else { setDestCoords(c); setDestSelected(true); }
            },
            (err) => toast.error("Pa ka jwenn pozisyon. Asire w bay pèmisyon an."),
            { enableHighAccuracy: true }
        );
    };

    const fetchHistory = async (phone) => {
        const p = phone || phoneInput || clientPhone;
        if (!p) return;
        setLoadingHistory(true);
        try {
            const res = await axios.get(`${API}/buyer/orders?phone=${encodeURIComponent(p.trim())}`);
            setRideHistory(res.data);
            if (!clientPhone) {
                setClientPhone(p);
                localStorage.setItem("buyer_phone", p);
            }
        } catch (err) {
            toast.error("Pa kapab chèche istorik kous yo.");
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSelectRideFromHistory = (orderId) => {
        setRideId(orderId);
        const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + `?rideId=${orderId}`;
        window.history.pushState({ path: newUrl }, '', newUrl);
    };

    const handleExitTracking = () => {
        setRideId(null);
        setTrackingRide(null);
        const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
        window.history.pushState({ path: newUrl }, '', newUrl);
    };
    return (
        <div className="min-h-screen bg-[#F5F8FA] flex flex-col font-sans">
            <Header />
            
            <main className="flex-1 w-full max-w-md mx-auto p-4 flex flex-col">
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 flex-1 overflow-hidden flex flex-col">
                    
                    {/* Render Tracking Screen if rideId is set */}
                    {rideId ? (
                        <div className="p-6 flex-1 flex flex-col h-full fade-in">
                            <div className="flex items-center justify-between mb-4">
                                <Button 
                                    onClick={handleExitTracking} 
                                    variant="ghost" 
                                    className="text-gray-500 hover:text-gray-700 p-0 hover:bg-transparent"
                                >
                                    <ArrowRight className="w-5 h-5 rotate-180 mr-1" /> Retounen
                                </Button>
                                <span className="text-xs font-bold text-gray-400">ID: {rideId.substring(0, 8)}...</span>
                            </div>

                            {trackingRide ? (
                                <div className="flex-1 flex flex-col h-full">
                                    {/* Status Card with 4-Step Live Tracking Stepper */}
                                    {(() => {
                                        const rawStatus = trackingRide.delivery_info?.status || trackingRide.status || 'pending';
                                        
                                        let currentStep = 0;
                                        let stepTitle = "Ap chèche chofè...";
                                        let stepDesc = "Nou ap voye demann ou an bay tout chofè ki pre ou yo...";
                                        let stepBadge = "Rechèch";

                                        if (rawStatus === 'pending_payment') {
                                            currentStep = 0;
                                            stepTitle = "Atant Peman";
                                            stepDesc = "Tanpri peye sou Moncash pou konfime kous la.";
                                            stepBadge = "Peman";
                                        } else if (rawStatus === 'accepted') {
                                            currentStep = 1;
                                            stepTitle = "Chofè Aksepte — Sou Wout";
                                            stepDesc = "Chofè a aksepte kous la epi li sou wout pou l vin pran w.";
                                            stepBadge = "Etap 1/4";
                                        } else if (['arrived', 'arrived_pickup', 'arrived_store'].includes(rawStatus)) {
                                            currentStep = 2;
                                            stepTitle = "Chofè a Rive Kote w Ye a!";
                                            stepDesc = "Chofè a rive nan pwen depa w la. Tanpri tcheke deyò a.";
                                            stepBadge = "Etap 2/4";
                                        } else if (['picked_up', 'in_transit', 'on_the_way'].includes(rawStatus)) {
                                            currentStep = 3;
                                            stepTitle = "Pasaje Monte — Vwayaj an Kou";
                                            stepDesc = "Kous la kòmanse! Chofè a ap mennen w nan destinasyon w.";
                                            stepBadge = "Etap 3/4";
                                        } else if (['delivered', 'completed'].includes(rawStatus)) {
                                            currentStep = 4;
                                            stepTitle = "Pasaje Depoze — Kous Fini!";
                                            stepDesc = "Ou rive nan destinasyon w avèk siksè! Mèsi dèske w te itilize BVG Link.";
                                            stepBadge = "Kous Fini";
                                        } else if (['cancelled', 'rejected'].includes(rawStatus)) {
                                            currentStep = -1;
                                            stepTitle = "Kous Anile";
                                            stepDesc = "Yo anile kous sa a.";
                                            stepBadge = "Anile";
                                        }

                                        const steps = [
                                            { num: 1, label: "Aksepte" },
                                            { num: 2, label: "Chofè Rive" },
                                            { num: 3, label: "Vwayaj" },
                                            { num: 4, label: "Depoze" }
                                        ];

                                        return (
                                            <div className="space-y-3 mb-4">
                                                <div className={`p-5 rounded-2xl text-white shadow-lg transition-all ${
                                                    currentStep === 4 
                                                    ? 'bg-gradient-to-br from-emerald-800 to-green-950 border border-emerald-500/40' 
                                                    : currentStep === 2 
                                                    ? 'bg-gradient-to-br from-cyan-900 to-slate-900 border border-cyan-500/40' 
                                                    : 'bg-gradient-to-br from-[#1D324F] to-[#152438] border border-blue-500/20'
                                                }`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-[9px] uppercase font-black tracking-widest text-[#FFB800] bg-white/10 px-2.5 py-0.5 rounded-full">
                                                            {stepBadge}
                                                        </span>
                                                        <span className="text-xs font-black text-slate-300">
                                                            {trackingRide.price_htg || trackingRide.delivery_info?.fee || 0} HTG
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-3 my-1">
                                                        {currentStep === 4 ? (
                                                            <CheckCircle className="w-6 h-6 text-green-400 shrink-0" />
                                                        ) : (
                                                            <div className="animate-pulse w-3.5 h-3.5 rounded-full bg-[#FFB800] shrink-0"></div>
                                                        )}
                                                        <h3 className="text-base sm:text-lg font-black uppercase tracking-tight leading-tight">
                                                            {stepTitle}
                                                        </h3>
                                                    </div>

                                                    <p className="text-xs text-slate-200 mt-1 leading-relaxed">
                                                        {stepDesc}
                                                    </p>

                                                    {/* 4-Step Stepper Progress Bar */}
                                                    {currentStep > 0 && currentStep <= 4 && (
                                                        <div className="mt-4 pt-3 border-t border-white/10">
                                                            <div className="flex items-center justify-between relative mb-1 px-1">
                                                                <div className="absolute top-1/2 left-4 right-4 h-1 bg-white/15 -translate-y-1/2 rounded-full -z-0">
                                                                    <div 
                                                                        className="h-full bg-[#FFB800] rounded-full transition-all duration-500"
                                                                        style={{ width: `${Math.min(100, Math.max(0, ((currentStep - 1) / 3) * 100))}%` }}
                                                                    />
                                                                </div>

                                                                {steps.map(s => {
                                                                    const isDone = currentStep >= s.num;
                                                                    const isCurrent = currentStep === s.num;
                                                                    return (
                                                                        <div key={s.num} className="relative z-10 flex flex-col items-center">
                                                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-[10px] transition-all shadow-md ${
                                                                                isCurrent 
                                                                                ? 'bg-[#FFB800] text-slate-950 ring-4 ring-[#FFB800]/30 scale-110' 
                                                                                : isDone 
                                                                                ? 'bg-emerald-500 text-white' 
                                                                                : 'bg-slate-700 text-slate-400 border border-white/10'
                                                                            }`}>
                                                                                {isDone && !isCurrent ? '✓' : s.num}
                                                                            </div>
                                                                            <span className={`text-[8px] font-black uppercase tracking-wider mt-1.5 ${
                                                                                isCurrent ? 'text-[#FFB800]' : isDone ? 'text-emerald-300' : 'text-slate-400'
                                                                            }`}>
                                                                                {s.label}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {trackingRide.delivery_info?.status === 'pending_payment' && trackingRide.redirect_url && (
                                                        <Button 
                                                            onClick={() => window.location.href = trackingRide.redirect_url}
                                                            className="w-full mt-4 bg-[#FFB800] hover:bg-[#e5a600] text-[#1D324F] font-black"
                                                        >
                                                            Peye Kounye a sou Moncash
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Driver Card */}
                                    {trackingRide.delivery_info?.driver_id && (
                                        <div className="bg-white border border-gray-100 shadow-sm p-4 rounded-2xl mb-4 flex items-center gap-4">
                                            {trackingRide.delivery_info.driver_photo ? (
                                                <img 
                                                    src={trackingRide.delivery_info.driver_photo} 
                                                    alt="Chofè" 
                                                    className="w-14 h-14 rounded-full object-cover border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-14 h-14 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center font-bold text-xl border">
                                                    {trackingRide.delivery_info.driver_name?.substring(0, 2).toUpperCase()}
                                                </div>
                                            )}
                                            
                                            <div className="flex-1">
                                                <h4 className="font-bold text-[#1D324F] text-sm">{trackingRide.delivery_info.driver_name}</h4>
                                                <p className="text-[11px] text-gray-400 font-bold mb-1">{trackingRide.delivery_info.vehicle_info}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <a 
                                                        href={`tel:${trackingRide.delivery_info.driver_phone}`} 
                                                        className="px-2.5 py-1 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg text-[11px] text-green-700 font-black flex items-center gap-1 transition-all"
                                                    >
                                                        <Phone className="w-3 h-3 fill-current" /> Rele
                                                    </a>
                                                    <a 
                                                        href={`https://wa.me/509${String(trackingRide.delivery_info.driver_phone || '').replace(/\D/g, '')}?text=${encodeURIComponent('Bonjou, se mwen menm ki te mande kous la sou BVGLink.')}`} 
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-[11px] font-black flex items-center gap-1 transition-all shadow-sm"
                                                    >
                                                        💬 WhatsApp
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Real-time map */}
                                    <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-inner flex-1 min-h-[250px] relative mb-4">
                                        <MapContainer 
                                            center={[trackingRide.seller_location?.lat || 18.5524, trackingRide.seller_location?.lng || -72.2891]} 
                                            zoom={13} 
                                            style={{ height: '300px', width: '100%' }}
                                        >
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            
                                            {/* Line connecting green and red points */}
                                            {trackingRide.seller_location && trackingRide.delivery_info?.customer_location && (
                                                <Polyline 
                                                    positions={[
                                                        [trackingRide.seller_location.lat, trackingRide.seller_location.lng],
                                                        [trackingRide.delivery_info.customer_location.lat, trackingRide.delivery_info.customer_location.lng]
                                                    ]}
                                                    pathOptions={{ color: '#1D324F', weight: 4, opacity: 0.8, dashArray: '6, 8' }}
                                                />
                                            )}

                                            {/* Pickup location (Green Marker) */}
                                            {trackingRide.seller_location && (
                                                <Marker position={[trackingRide.seller_location.lat, trackingRide.seller_location.lng]} icon={greenMarkerIcon}>
                                                    <Popup>Pwen depa (Vèt)</Popup>
                                                </Marker>
                                            )}
                                            
                                            {/* Dropoff location (Red Marker) */}
                                            {trackingRide.delivery_info?.customer_location && (
                                                <Marker position={[trackingRide.delivery_info.customer_location.lat, trackingRide.delivery_info.customer_location.lng]} icon={redMarkerIcon}>
                                                    <Popup>Destinasyon (Wouj)</Popup>
                                                </Marker>
                                            )}
                                            
                                            {/* Driver Live Location */}
                                            {trackingRide.delivery_info?.driver_lat && trackingRide.delivery_info?.driver_lng && (
                                                <Marker position={[trackingRide.delivery_info.driver_lat, trackingRide.delivery_info.driver_lng]} icon={carMarkerIcon}>
                                                    <Popup>Chofè a la</Popup>
                                                </Marker>
                                            )}

                                            <MapBoundsManager 
                                                pickup={trackingRide.seller_location} 
                                                dropoff={trackingRide.delivery_info?.customer_location} 
                                                driver={trackingRide.delivery_info?.driver_lat ? { lat: trackingRide.delivery_info.driver_lat, lng: trackingRide.delivery_info.driver_lng } : null}
                                            />
                                        </MapContainer>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center py-10">
                                    <RefreshCw className="w-10 h-10 text-[#FFB800] animate-spin mb-4" />
                                    <p className="text-gray-500 font-bold">Ap chèche detay kous la...</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col">
                            {/* Navigation Tabs */}
                            <div className="flex border-b border-gray-100 bg-gray-50/50">
                                <button 
                                    onClick={() => setActiveTab('booking')}
                                    className={`flex-1 py-4 text-center font-bold text-sm transition-all border-b-2 flex items-center justify-center gap-2 ${activeTab === 'booking' ? 'text-[#FFB800] border-[#FFB800] bg-white' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                                >
                                    <MapPinned className="w-4 h-4" /> Demann Kous
                                </button>
                                <button 
                                    onClick={() => { setActiveTab('history'); fetchHistory(); }}
                                    className={`flex-1 py-4 text-center font-bold text-sm transition-all border-b-2 flex items-center justify-center gap-2 ${activeTab === 'history' ? 'text-[#FFB800] border-[#FFB800] bg-white' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                                >
                                    <Clock className="w-4 h-4" /> Istorik Kous
                                </button>
                            </div>

                            {/* Booking Tab Content */}
                            {activeTab === 'booking' && (
                                <div className="flex-1 flex flex-col">
                                    {/* Stepper Header */}
                                    <div className="flex items-center justify-center gap-2 pt-6 pb-2">
                                        <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-[#FFB800]' : 'bg-gray-200'}`}></div>
                                        <div className="w-8 h-[2px] bg-gray-200"><div className={`h-full ${step >= 2 ? 'bg-[#FFB800]' : ''}`}></div></div>
                                        <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-[#FFB800]' : 'bg-gray-200'}`}></div>
                                        <div className="w-8 h-[2px] bg-gray-200"><div className={`h-full ${step >= 3 ? 'bg-[#FFB800]' : ''}`}></div></div>
                                        <div className={`w-3 h-3 rounded-full ${step >= 3 ? 'bg-[#FFB800]' : 'bg-gray-200'}`}></div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6 flex-1 flex flex-col">
                                        
                                        {/* STEP 1: ORIGIN (GREEN MARKER) */}
                                        {step === 1 && (
                                            <div className="flex-1 flex flex-col h-full fade-in">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <h1 className="text-2xl font-black text-[#1D324F]">Ki kote ou ye?</h1>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowDriversList(!showDriversList)}
                                                        className="px-3 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-black rounded-full flex items-center gap-1.5 transition-all shadow-sm active:scale-95"
                                                    >
                                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                                        <span>{onlineDrivers.length} {onlineDrivers.length === 1 ? 'Chofè' : 'Chofè'} An Liy</span>
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-500 font-medium mb-4">Chwazi kote ou vle yo vin chèche ou (Pwen Vèt)</p>

                                                {/* EXPANDABLE ONLINE DRIVERS LIST */}
                                                {showDriversList && (
                                                    <div className="mb-5 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 p-4 rounded-2xl shadow-xl text-white animate-in fade-in zoom-in-95 duration-200">
                                                        <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-white/10">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                                                                <h3 className="font-black text-xs uppercase tracking-wider text-emerald-400">
                                                                    Chofè ki An Liy Kounye a ({onlineDrivers.length})
                                                                </h3>
                                                            </div>
                                                            <button 
                                                                onClick={() => setShowDriversList(false)}
                                                                className="text-slate-400 hover:text-white text-xs font-black px-2 py-0.5 rounded-lg bg-white/5"
                                                            >
                                                                Fèmen ✕
                                                            </button>
                                                        </div>

                                                        {onlineDrivers.length === 0 ? (
                                                            <p className="text-xs text-slate-400 text-center py-3 font-medium">
                                                                Pa gen chofè an liy nan moman an. N ap notifye yo kou w fè demann lan!
                                                            </p>
                                                        ) : (
                                                            <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                                                                {onlineDrivers.map(d => {
                                                                    const zone = d.commune || d.zone || d.department || 'Pòtoprens';
                                                                    const isMoto = (d.vehicle_type || '').toLowerCase().includes('moto');
                                                                    return (
                                                                        <div key={d.id} className="flex items-center justify-between p-2.5 bg-white/5 hover:bg-white/10 rounded-xl border border-white/5 transition-all">
                                                                            <div className="flex items-center gap-3">
                                                                                {d.profile_photo ? (
                                                                                    <img src={d.profile_photo} alt={d.fullname} className="w-10 h-10 rounded-full object-cover border border-emerald-500/40" />
                                                                                ) : (
                                                                                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-black text-sm">
                                                                                        {(d.fullname || 'C').slice(0, 1).toUpperCase()}
                                                                                    </div>
                                                                                )}
                                                                                <div>
                                                                                    <div className="flex items-center gap-2">
                                                                                        <p className="font-black text-xs text-white">{d.fullname || d.name}</p>
                                                                                        <span className="text-[9px] font-black px-1.5 py-0.2 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                                                                            {isMoto ? '🏍️ Moto' : '🚗 Machin'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <p className="text-[10px] font-bold text-amber-400 flex items-center gap-1 mt-0.5">
                                                                                        <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                                                                        <span>Zòn: {zone}</span>
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            <span className="text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full border border-emerald-500/20">
                                                                                🟢 An Liy
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div 
                                                    onClick={() => getMyGPS(true)} 
                                                    className="bg-[#EFFFF6] p-4 rounded-xl border border-green-100 cursor-pointer flex items-center gap-4 mb-4 hover:shadow-md transition-all active:scale-[0.98]"
                                                >
                                                    <div className="w-10 h-10 bg-[#29C271] rounded-full flex flex-col items-center justify-center text-white shrink-0 shadow-sm border-[3px] border-[#a5ecca]">
                                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs font-semibold text-gray-500">Pwen depa (Vèt)</p>
                                                        <p className="text-sm font-bold text-gray-900">{originSelected ? 'Pozisyon anrejistre (GPS)' : 'Sèvi ak lokalizasyon m'}</p>
                                                    </div>
                                                </div>

                                                <Input 
                                                    placeholder="Ekri adrès depa presi... (Egz: #14 Delma 33)" 
                                                    value={originAddress}
                                                    onChange={(e)=>setOriginAddress(e.target.value)}
                                                    className="h-12 bg-white rounded-xl mb-4 border-gray-200 text-sm font-medium focus:ring-[#FFB800]"
                                                />
                                                
                                                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-inner flex-1 min-h-[250px] relative">
                                                    <MapContainer center={[18.5524, -72.2891]} zoom={13} style={{ height: '100%', minHeight: '250px', width: '100%' }}>
                                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                        <LocationMarker 
                                                            position={originSelected ? originCoords : null} 
                                                            setPosition={(pos) => { setOriginCoords(pos); setOriginSelected(true); }}
                                                            icon={greenMarkerIcon}
                                                            label="Pwen Depa (Vèt)"
                                                        />

                                                        {/* LIVE ONLINE DRIVER MARKERS */}
                                                        {onlineDrivers
                                                            .filter(d => d.current_lat && d.current_lng)
                                                            .map(d => (
                                                                <Marker 
                                                                    key={d.id} 
                                                                    position={[d.current_lat, d.current_lng]} 
                                                                    icon={carMarkerIcon}
                                                                >
                                                                    <Popup>
                                                                        <div className="p-1 font-sans text-center">
                                                                            <p className="font-black text-xs text-[#1D324F]">🟢 {d.fullname || d.name}</p>
                                                                            <p className="text-[10px] font-bold text-slate-500 mt-0.5">
                                                                                {(d.vehicle_type || '').toLowerCase().includes('moto') ? '🏍️ Moto' : '🚗 Machin'}
                                                                            </p>
                                                                            <p className="text-[9px] font-black text-amber-600 mt-0.5 uppercase">
                                                                                📍 {d.commune || d.zone || d.department || 'Prè w'}
                                                                            </p>
                                                                        </div>
                                                                    </Popup>
                                                                </Marker>
                                                            ))
                                                        }
                                                    </MapContainer>
                                                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full font-bold text-xs shadow-md z-[400] flex items-center gap-2">
                                                        <div className="w-2.5 h-2.5 bg-[#29C271] rounded-full"></div>
                                                        Touche pwen depa (Vèt) sou kat la 
                                                    </div>
                                                </div>

                                                <Button 
                                                    disabled={!originSelected && !originAddress} 
                                                    onClick={()=>setStep(2)} 
                                                    className="w-full mt-6 h-14 bg-[#FFB800] hover:bg-[#e5a600] disabled:bg-gray-200 text-white font-bold text-lg rounded-xl transition-all shadow-md active:scale-[0.98]"
                                                >
                                                    Kontinye <ArrowRight className="w-5 h-5 ml-2" />
                                                </Button>
                                            </div>
                                        )}

                                        {/* STEP 2: DESTINATION (RED MARKER + CONNECTING LINE) */}
                                        {step === 2 && (
                                            <div className="flex-1 flex flex-col h-full fade-in z-10 w-full relative">
                                                <Button variant="ghost" onClick={()=>setStep(1)} className="absolute -top-4 -left-4 text-gray-400 p-0 hover:bg-transparent"><ArrowRight className="w-6 h-6 rotate-180" /></Button>
                                                
                                                <h1 className="text-2xl font-black text-[#1D324F] mb-1 pl-6">Kote ou prale?</h1>
                                                <p className="text-sm text-gray-500 font-medium mb-6 pl-6">Chwazi destinasyon ou (Pwen Wouj)</p>

                                                <Input 
                                                    placeholder="Ekri adrès destinasyon an..." 
                                                    value={destAddress}
                                                    onChange={(e)=>setDestAddress(e.target.value)}
                                                    className="h-12 bg-white rounded-xl mb-4 border-gray-200 text-sm font-medium focus:ring-[#FFB800]"
                                                />
                                                
                                                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-inner flex-1 min-h-[250px] relative">
                                                    <MapContainer center={originCoords} zoom={13} style={{ height: '100%', minHeight: '250px', width: '100%' }}>
                                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                        
                                                        {/* Green Marker for Departure */}
                                                        {originSelected && (
                                                            <Marker position={originCoords} icon={greenMarkerIcon}>
                                                                <Popup>Pwen Depa (Vèt)</Popup>
                                                            </Marker>
                                                        )}
                                                        
                                                        {/* Red Marker for Destination */}
                                                        <LocationMarker 
                                                            position={destSelected ? destCoords : null} 
                                                            setPosition={(pos) => { setDestCoords(pos); setDestSelected(true); }}
                                                            icon={redMarkerIcon}
                                                            label="Destinasyon (Wouj)"
                                                        />

                                                        {/* Traced Connection Line between Green Point and Red Point */}
                                                        {originSelected && destSelected && (
                                                            <Polyline 
                                                                positions={[
                                                                    [originCoords.lat, originCoords.lng],
                                                                    [destCoords.lat, destCoords.lng]
                                                                ]}
                                                                pathOptions={{ color: '#1D324F', weight: 4, opacity: 0.8, dashArray: '6, 8' }}
                                                            />
                                                        )}
                                                    </MapContainer>
                                                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full font-bold text-xs shadow-md z-[400] flex items-center gap-2">
                                                        <div className="w-2.5 h-2.5 bg-[#E33535] rounded-full"></div>
                                                        Touche destinasyon (Wouj) sou kat la
                                                    </div>
                                                </div>

                                                <Button 
                                                    disabled={!destSelected && !destAddress} 
                                                    onClick={()=>setStep(3)} 
                                                    className="w-full mt-6 h-14 bg-[#FFB800] hover:bg-[#e5a600] disabled:bg-gray-200 text-white font-bold text-lg rounded-xl transition-all shadow-md active:scale-[0.98]"
                                                >
                                                    Kontinye <ArrowRight className="w-5 h-5 ml-2" />
                                                </Button>
                                            </div>
                                        )}
                                        {/* STEP 3: CONFIRM & BOOK */}
                                        {step === 3 && (
                                            <form onSubmit={handleCheckout} className="flex-1 flex flex-col h-full fade-in relative">
                                                <Button variant="ghost" onClick={()=>setStep(2)} className="absolute -top-4 -left-4 text-gray-400 p-0 hover:bg-transparent"><ArrowRight className="w-6 h-6 rotate-180" /></Button>
                                                
                                                <h1 className="text-2xl font-black text-[#1D324F] mb-1 pl-6">Konfime Kous La</h1>
                                                <p className="text-sm text-gray-500 font-medium mb-4 pl-6">Mete enfòmasyon w pou finalize rezèvasyon an</p>

                                                {/* Mini Route Map Preview with Green and Red points & Polyline */}
                                                <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-inner h-36 relative mb-4">
                                                    <MapContainer center={originCoords} zoom={13} style={{ height: '100%', width: '100%' }}>
                                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                        {originSelected && (
                                                            <Marker position={originCoords} icon={greenMarkerIcon}>
                                                                <Popup>Depa (Vèt)</Popup>
                                                            </Marker>
                                                        )}
                                                        {destSelected && (
                                                            <Marker position={destCoords} icon={redMarkerIcon}>
                                                                <Popup>Destinasyon (Wouj)</Popup>
                                                            </Marker>
                                                        )}
                                                        {originSelected && destSelected && (
                                                            <Polyline 
                                                                positions={[
                                                                    [originCoords.lat, originCoords.lng],
                                                                    [destCoords.lat, destCoords.lng]
                                                                ]}
                                                                pathOptions={{ color: '#1D324F', weight: 4, opacity: 0.8, dashArray: '6, 8' }}
                                                            />
                                                        )}
                                                        <MapBoundsManager pickup={originCoords} dropoff={destCoords} />
                                                    </MapContainer>
                                                </div>

                                                {/* Booking Summary */}
                                                <div className="bg-[#F8FAFC] border border-gray-100 rounded-2xl p-4 mb-4 space-y-3">
                                                    <div className="flex items-start gap-3">
                                                        <MapPin className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Pwen Depa (Vèt)</p>
                                                            <p className="text-xs font-bold text-[#1D324F]">{originAddress || "Sou Map La"}</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-start gap-3 border-t border-gray-50 pt-3">
                                                        <Navigation className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Destinasyon (Wouj)</p>
                                                            <p className="text-xs font-bold text-[#1D324F]">{destAddress || "Sou Map La"}</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center border-t border-gray-50 pt-3 text-xs">
                                                        <span className="font-bold text-gray-400">Distans:</span>
                                                        <span className="font-black text-[#1D324F]">{distance.toFixed(2)} km</span>
                                                    </div>
                                                </div>

                                                {/* Transport Type Selector */}
                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div 
                                                        onClick={() => setTransportType('moto')}
                                                        className={`border-2 rounded-2xl p-3 flex flex-col items-center justify-center cursor-pointer transition-all hover:shadow-sm ${transportType === 'moto' ? 'border-[#FFB800] bg-[#FFFDF6]' : 'border-gray-100 bg-white'}`}
                                                    >
                                                        <Navigation2 className="w-5 h-5 text-[#FFB800] mb-1" />
                                                        <span className="font-black text-xs text-[#1D324F]">Moto</span>
                                                        <span className="text-[10px] text-gray-400">25 HTG / km</span>
                                                    </div>
                                                    <div 
                                                        onClick={() => setTransportType('oto')}
                                                        className={`border-2 rounded-2xl p-3 flex flex-col items-center justify-center cursor-pointer transition-all hover:shadow-sm ${transportType === 'oto' ? 'border-[#FFB800] bg-[#FFFDF6]' : 'border-gray-100 bg-white'}`}
                                                    >
                                                        <CarFront className="w-5 h-5 text-[#1D324F] mb-1" />
                                                        <span className="font-black text-xs text-[#1D324F]">Machin</span>
                                                        <span className="text-[10px] text-gray-400">45 HTG / km</span>
                                                    </div>
                                                </div>

                                                {/* ── OPTION 6: LÈ KOUS LA AP FÈT (IMEDYAT VS REZÈVASYON) ── */}
                                                <div className="mb-4 bg-slate-50 border border-gray-100 p-3.5 rounded-2xl space-y-3">
                                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider block">
                                                        ⏰ Ki lè w vle kous la fèt?
                                                    </label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setScheduleType("now")}
                                                            className={`p-2.5 rounded-xl border-2 font-black text-xs uppercase flex items-center justify-center gap-1.5 transition-all ${
                                                                scheduleType === "now"
                                                                ? "border-[#FFB800] bg-[#FFFDF6] text-[#1D324F] shadow-sm"
                                                                : "border-gray-200 bg-white text-gray-400"
                                                            }`}
                                                        >
                                                            <span>⚡</span>
                                                            <span>Kounye a (Imedyat)</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setScheduleType("later")}
                                                            className={`p-2.5 rounded-xl border-2 font-black text-xs uppercase flex items-center justify-center gap-1.5 transition-all ${
                                                                scheduleType === "later"
                                                                ? "border-cyan-500 bg-cyan-50 text-cyan-900 shadow-sm"
                                                                : "border-gray-200 bg-white text-gray-400"
                                                            }`}
                                                        >
                                                            <span>⏰</span>
                                                            <span>Rezève pou Pita</span>
                                                        </button>
                                                    </div>

                                                    {scheduleType === "later" && (
                                                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200/60 animate-in fade-in duration-200">
                                                            <div>
                                                                <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Dat</label>
                                                                <input
                                                                    type="date"
                                                                    value={scheduledDate}
                                                                    onChange={e => setScheduledDate(e.target.value)}
                                                                    className="w-full h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-800 outline-none"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">Lè</label>
                                                                <input
                                                                    type="time"
                                                                    value={scheduledTime}
                                                                    onChange={e => setScheduledTime(e.target.value)}
                                                                    className="w-full h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-800 outline-none"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Client Inputs */}
                                                <div className="space-y-3 mb-4">
                                                    <div>
                                                        <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">Non w</label>
                                                        <Input 
                                                            placeholder="Antre non w" 
                                                            value={clientName}
                                                            onChange={(e) => setClientName(e.target.value)}
                                                            required
                                                            className="h-11 bg-white rounded-xl border-gray-200 text-sm font-medium focus:ring-[#FFB800]"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">Nimewo Telefòn ou (Moncash)</label>
                                                        <Input 
                                                            placeholder="Egz: 38574638" 
                                                            value={clientPhone}
                                                            onChange={(e) => setClientPhone(e.target.value)}
                                                            required
                                                            className="h-11 bg-white rounded-xl border-gray-200 text-sm font-medium focus:ring-[#FFB800]"
                                                        />
                                                    </div>

                                                    {/* ── OPTION 5: KÒMANDE POU YON LÒT MOUN ── */}
                                                    <div className="pt-2">
                                                        <label 
                                                            onClick={() => setIsForSomeoneElse(!isForSomeoneElse)}
                                                            className="flex items-center gap-2.5 p-3 rounded-xl bg-orange-50/70 border border-orange-100 cursor-pointer select-none transition-all hover:bg-orange-100/70"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isForSomeoneElse}
                                                                onChange={() => {}}
                                                                className="w-4 h-4 rounded text-[#FFB800] focus:ring-[#FFB800] border-gray-300"
                                                            />
                                                            <span className="text-xs font-black text-amber-900">
                                                                👤 M ap kòmande pou yon lòt moun (Fanmi / Zanmi)
                                                            </span>
                                                        </label>

                                                        {isForSomeoneElse && (
                                                            <div className="mt-3 p-3.5 bg-amber-50/50 border border-amber-200/80 rounded-2xl space-y-2.5 animate-in fade-in duration-200">
                                                                <p className="text-[10px] font-bold text-amber-800">
                                                                    Chofè a ap rele nimewo sa a lè l rive nan pwen depa a:
                                                                </p>
                                                                <div>
                                                                    <label className="text-[9px] font-black text-gray-500 uppercase block mb-1">Non Moun k ap vwayaje a *</label>
                                                                    <Input
                                                                        placeholder="Egz: Marie Paul"
                                                                        value={passengerName}
                                                                        onChange={e => setPassengerName(e.target.value)}
                                                                        className="h-10 bg-white border-amber-200 text-xs font-bold"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-[9px] font-black text-gray-500 uppercase block mb-1">Telefòn Moun nan *</label>
                                                                    <Input
                                                                        placeholder="Egz: 509-3xxx-xxxx"
                                                                        value={passengerPhone}
                                                                        onChange={e => setPassengerPhone(e.target.value)}
                                                                        className="h-10 bg-white border-amber-200 text-xs font-bold"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                                                    <div>
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase block">Total pou Peye</span>
                                                        <span className="text-2xl font-black text-[#1D324F]">{formatPrice(price)} HTG</span>
                                                    </div>
                                                    <Button 
                                                        type="submit"
                                                        disabled={processing}
                                                        className="h-12 bg-[#FFB800] hover:bg-[#e5a600] text-white font-black text-base rounded-xl px-6 shadow-md transition-all active:scale-[0.98]"
                                                    >
                                                        {processing ? (
                                                            <RefreshCw className="w-5 h-5 animate-spin" />
                                                        ) : (
                                                            <>Kòmande <CheckCircle className="w-5 h-5 ml-2" /></>
                                                        )}
                                                    </Button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* History Tab Content */}
                            {activeTab === 'history' && (
                                <div className="p-6 flex-1 flex flex-col h-full overflow-hidden">
                                    {!clientPhone ? (
                                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                                            <Phone className="w-12 h-12 text-gray-300 mb-4" />
                                            <h3 className="font-bold text-[#1D324F] mb-1">Konekte pou wè istorik ou</h3>
                                            <p className="text-xs text-gray-400 max-w-[200px] mb-6">Mete nimewo telefòn ou pou nou ka jwenn vwayaj ou yo.</p>
                                            
                                            <div className="flex gap-2 w-full">
                                                <Input 
                                                    placeholder="Egz: 38574638" 
                                                    value={phoneInput}
                                                    onChange={(e)=>setPhoneInput(e.target.value)}
                                                    className="h-12 bg-white rounded-xl border-gray-200 focus:ring-[#FFB800]"
                                                />
                                                <Button 
                                                    onClick={() => fetchHistory(phoneInput)}
                                                    className="h-12 bg-[#FFB800] hover:bg-[#e5a600] text-white font-bold rounded-xl px-6"
                                                >
                                                    Chèche
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex flex-col h-full overflow-hidden">
                                            <div className="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded-xl">
                                                <span className="text-xs font-bold text-gray-500">Telefòn: {clientPhone}</span>
                                                <Button 
                                                    onClick={() => { setClientPhone(""); localStorage.removeItem("buyer_phone"); }}
                                                    variant="ghost" 
                                                    className="text-xs text-red-500 hover:text-red-700 h-auto p-0 hover:bg-transparent"
                                                >
                                                    Chanje
                                                </Button>
                                            </div>

                                            {loadingHistory ? (
                                                <div className="flex-1 flex flex-col items-center justify-center">
                                                    <RefreshCw className="w-8 h-8 text-[#FFB800] animate-spin mb-2" />
                                                    <p className="text-xs text-gray-400 font-bold">Ap chèche istorik...</p>
                                                </div>
                                            ) : (
                                                <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                                                    {rideHistory.filter(o => o.product_title && (o.product_title.startsWith('Transp') || o.delivery_info?.type === 'transport' || o.delivery_info?.destination)).length === 0 ? (
                                                        <div className="text-center py-10 text-gray-400 text-sm">
                                                            Pa gen okenn kous ki anrejistre pou nimewo sa a.
                                                        </div>
                                                    ) : (
                                                        rideHistory
                                                            .filter(o => o.product_title && (o.product_title.startsWith('Transp') || o.delivery_info?.type === 'transport' || o.delivery_info?.destination))
                                                            .map((ride) => (
                                                                <div key={ride.id} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all">
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <span className="text-[10px] font-bold text-gray-400">
                                                                            {new Date(ride.created_at).toLocaleString('fr-HT', { dateStyle: 'short', timeStyle: 'short' })}
                                                                        </span>
                                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase ${
                                                                            ride.delivery_info?.status === 'completed' || ride.delivery_info?.status === 'delivered' ? 'bg-green-50 text-green-600' :
                                                                            ride.delivery_info?.status === 'cancelled' || ride.delivery_info?.status === 'rejected' ? 'bg-red-50 text-red-600' :
                                                                            ride.delivery_info?.status === 'pending_payment' ? 'bg-orange-50 text-orange-600' :
                                                                            'bg-blue-50 text-blue-600'
                                                                        }`}>
                                                                            {ride.delivery_info?.status || ride.status}
                                                                        </span>
                                                                    </div>
                                                                    
                                                                    <div className="text-xs font-bold text-[#1D324F] mb-1.5 flex flex-col gap-0.5">
                                                                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Depa: {ride.delivery_info?.address}</span>
                                                                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Arive: {ride.delivery_info?.destination}</span>
                                                                    </div>

                                                                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-50">
                                                                        <span className="text-sm font-black text-[#1D324F]">{ride.price_htg || ride.price} HTG</span>
                                                                        {ride.delivery_info?.status !== 'completed' && ride.delivery_info?.status !== 'delivered' && ride.delivery_info?.status !== 'cancelled' && (
                                                                            <Button 
                                                                                onClick={() => handleSelectRideFromHistory(ride.id)}
                                                                                className="h-8 bg-[#FFB800] hover:bg-[#e5a600] text-white font-bold text-xs rounded-lg px-4"
                                                                            >
                                                                                Swiv Kous
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    
                </div>
            </main>
        </div>
    );
};
